import { Typography } from '@mui/material'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import { useCallback, useEffect, useState } from 'react'
import EventDialog from './EventDialog'
import { apiGet, apiPost, apiPut } from '../generic/Api_Functions'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import AlertUI from '../generic/AlertUI'
import moment from 'moment'
import { EVENT_DIALOG_TAB_KEYS, getEventDialogTabKey, getEventDialogTabIndex, DEFAULT_EVENT_DIALOG_TAB_INDEX } from './utils/eventDialogTabs'
import { isJobPriorityValid, toJobPriorityPayload } from './utils/jobPriority'

/**
* @typedef {import("../generic/utilities/objectStructures/planner/event").Event} Event Event object
* @typedef {import("../generic/utilities/objectStructures/mos/FabricationOrder").FabricationOrder} FabricationOrder Fabrication Order object
* @typedef {import("../generic/utilities/objectStructures/planner/planification").Planification} Planification Planification object
* @typedef {import("../generic/utilities/objectStructures/planner/event").Job} Job Job object
* @typedef {import("../generic/utilities/objectStructures/planner/event").PinnedSettings} PinnedSettings PinnedSettings object
* @typedef {import("../generic/utilities/objectStructures/planner/jobSettings").JobSettings} JobSettings JobSettings object
**/

/**
 * Planner event dialog component
 * @param {Object} props
 * @param {Object} props.eventInfoOnClick EventInfo object generated after click on a event on Calendar
 * @param {function()} props.showAlert Function to show alert
 * @param {Planification} props.currentPlanification Current planification object
 * @returns
 */
const PlannerEventDialog = ({
  eventInfoOnClick,
  showAlert,
  currentPlanification,
  onEventChange,
  reloadCalendar,
  setResourceFamily,
  calendarRef,
  setIsFamilyViewActive,
  isThisEventFamily
}) => {
  const { t_ } = useSmartTranslation()
  /** @type {[Event, React.Dispatch<SetStateAction<Event>>]} */
  const [editingEvent, setEditingEvent] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [selectedTab, setSelectedTab] = useState(DEFAULT_EVENT_DIALOG_TAB_INDEX)
  /** @type {[Job, React.Dispatch<SetStateAction<Job>>]} */
  const [newPinJobSettings, setNewPinJobSettings] = useState()
  const [AlertElementDialog, showAlertDialog] = AlertUI()
  /** @type {[PinnedSettings, React.Dispatch<SetStateAction<PinnedSettings>>]} */
  const [pinSettings, setPinSettings] = useState()
  /** @type {[JobSettings, React.Dispatch<SetStateAction<JobSettings>>]} */
  const [jobSettings, setJobSettings] = useState()
  /** @type {[JobSettings, React.Dispatch<SetStateAction<JobSettings>>]} */
  const [newJobSettings, setNewJobSettings] = useState()
  const [showJobSettingsErrors, setShowJobSettingsErrors] = useState(false)

  const getJobInfo = async (e) => {
    const partId = e.partId == null ? 0 : e.partId
    const data = { ...e, ...(await apiGet('planner/part/' + partId)) }
    data.taskId = data.taskReferenceId
    setEditingEvent(data)
  }

  useEffect(() => {
    if (eventInfoOnClick) {
      // Historia 140/#2924: the side panel's foot actions name the tab they are opening, so the
      // dialog has to land on it. Without this the key travelled and nobody listened, and all three
      // buttons opened the first tab -- each one promising something it did not do. An unknown or
      // absent key falls back to the default tab, which is the plain click from the calendar.
      setSelectedTab(getEventDialogTabIndex(eventInfoOnClick.initialTabKey))
      getJobInfo(eventInfoOnClick)
    }
  }, [eventInfoOnClick])

  useEffect(() => {
    if (editingEvent) {
      setOpenDialog(true)
    }
  }, [editingEvent])

  const onChangeSelectedTab = (newTab) => {
    setSelectedTab(newTab)
  }

  // Story 96 (CU1): the "Job Settings" tab reads by JOB, through a path of its own. The pin tab's
  // read is purely about the plan's task (dates and pins) and carries no job data at all, so it
  // cannot serve this tab.
  useEffect(() => {
    const getJobSettings = async () => {
      const data = await apiGet('planner/jobSettings/' + editingEvent.jobReferenceId)
      setJobSettings(data)
      // Story 146 / S68 (RN31.1/RN28/D31): an EXTERNAL job is planned on an external asset, so it has
      // no preferred one. The EDITED state is born without it -- a single point that gives both the
      // empty selector and, because the write is a replacement, the `preferenceDevice: ''` that
      // RELEASES whatever was fixed before the node was marked. The read (`jobSettings`) is kept
      // verbatim so the tab can still tell why. `extern` itself is read-only and never sent (RN64.1).
      setNewJobSettings(data?.extern === true ? { ...data, preferenceDevice: '' } : data)
    }
    if (editingEvent?.jobReferenceId !== undefined && editingEvent?.jobReferenceId !== null) {
      getJobSettings()
    }
  }, [editingEvent?.jobReferenceId])

  const onJobEditAccept = async () => {
    const priority = parseInt(editingEvent.priority)
    if (isNaN(priority) || (priority < 1 || priority > 10)) {
      setEditingEvent({ ...editingEvent, showErrorsJob: true })
      return
    }
    await apiPost('planner/part/' + editingEvent.partId, editingEvent)
    return true
  }

  // Story 96 (R3): the two branches that handled the fabrication-order status and the reported
  // quantities have been removed along with the index-based dispatch. They were already unreachable
  // -- this dialog has only ever rendered the Part and Pin tabs, so `selectedTab` could never be 2
  // or 3 -- and leaving dead cases behind a switch that the new first tab shifts is precisely how a
  // wrong branch gets executed. Those flows live in MOS, which is where they are reached from.

  const savePinSettings = async () => {
    const newDateStart = new Date(newPinJobSettings.dateStart)
    const gap = new Date(pinSettings.currentJob.dateEnd) - new Date(pinSettings.currentJob.dateStart)
    const newDateEnd = new Date(newDateStart.getTime() + gap)

    // Set new end date
    newPinJobSettings.dateEnd = newDateEnd
    const deliveryDateFO = moment(newPinJobSettings.foDate).utc()
    const end = moment(newPinJobSettings.dateEnd).utc()
    const newLate = end > deliveryDateFO
    let result
    const updatedNewPinJobSettings = { ...newPinJobSettings, newLate }

    // Set pinned date. Story 96 (RN27/INV6): the machine pin is retired, so nothing writes it any
    // more. The machine still travels in the payload because this save path requires it, but as the
    // job's CURRENT machine, unchanged -- this tab never moves a job (CU9/RN34).
    if (newPinJobSettings.pinnedDate !== false && newPinJobSettings.pinnedDate !== null && newPinJobSettings.pinnedDate !== undefined) {
      updatedNewPinJobSettings.pinnedDate = newPinJobSettings.dateStart
    } else {
      updatedNewPinJobSettings.pinnedDate = null
    }

    await apiPut('planner/updatePlanificationJob/' + currentPlanification.id, updatedNewPinJobSettings).then(() => {
      showAlert({ message: t_('Updated correctly.'), severity: 'success' })
      onEventChange(newPinJobSettings)
      result = true
    }).catch((e) => {
      showAlert({ title: t_('Error updating pin settings'), message: e.message, severity: e.name })
    })
    return result
  }

  /**
   * Story 96 — surfaces a rejection of the job settings write with the SERVER's own wording,
   * translated, following the same shape the MOS status-change path uses.
   *
   * It matters here beyond the usual validation rejections: `Device.name` carries **no uniqueness
   * constraint**, so Sun resolves the preferred machine by name and **rejects an ambiguous one**
   * instead of picking an arbitrary homonym. That is a deliberate 400 the planner has to be able to
   * read — the preferred machine persists in the job and survives replanning, so silently fixing the
   * wrong machine would be a durable, hard-to-diagnose error. The selector does NOT deduplicate
   * homonyms on purpose: hiding the ambiguity would make this rejection come out of nowhere.
   */
  // `isNaN(null)` is FALSE (Number(null) is 0), so a bare `!isNaN(id)` treats a null id as present.
  // These ids decide whether the draft is relocated and how widely, so the check has to be strict.
  const isPresentId = (value) => value !== null && value !== undefined && !isNaN(value)

  const showJobSettingsError = (error) => {
    console.error('Error updating job settings:', error)
    // Sun answers with `{ code, message }`: `code` is the stable contract and IS the i18n key, while
    // `message` is developer detail that names the offending value and must never reach the screen.
    // `apiF` puts that developer message into `error.message`, so keying off it -- as this did before
    // the contract landed -- would show exactly what Sun says not to show.
    const code = error.data?.code
    return code ? t_(code) : t_('Error updating job settings')
  }

  /**
   * Story 96 (CU2/CU3/CU4) — saves the three job-owned fields as ONE act.
   *
   * ⚠️ The write is a **REPLACEMENT, not a patch**: omitting a field CLEARS it (an absent
   * `preferenceDevice` RELEASES the machine). So all three always travel, even the untouched ones.
   *
   * `planificationId` goes with them so **Sun** relocates the draft when the machine changed — the
   * relocation is server-side, inside this same call, not a second request from here. Without it the
   * job is written and the plan is not touched at all (RN34/CU4, which is also what clearing the
   * machine does — RN23 moves nothing).
   *
   * **No task id travels.** Sun resolves the affected tasks from the job and the plan; what it takes
   * instead is `partId`, which SCOPES the move (D29/RN21): in a plan by PART only the piece the dialog
   * was opened from moves, and in a plan by FO the part is omitted so the job's tasks move.
   *
   * RN32: everything is validated before anything is written, so a bad priority writes neither the
   * remark nor the machine. RN31: Sun loads the job and modifies only these three fields.
   *
   * E7/D33 (S30): a failed relocation comes back as `warningCode: 'RELOCATION_FAILED'` in a **200**,
   * not as a rejection — the three fields WERE saved and the edit was not lost. Warn and carry on:
   * nothing is reverted and there is no retry, automatic or offered. (A failure of Sun's own
   * asynchronous consumer is invisible from here: documented limit R2.)
   */
  const saveJobSettings = async () => {
    if (!isJobPriorityValid(newJobSettings?.jobPriority)) {
      setShowJobSettingsErrors(true)
      return
    }
    setShowJobSettingsErrors(false)

    // `planificationId` decides ON ITS OWN whether Sun relocates; there is no "both or neither" any
    // more. Sun resolves the tasks from the job and the plan, so NO task id travels.
    const canRelocate = isPresentId(currentPlanification?.id)
    // D29/RN21 — `partId` SCOPES the move, and its absence means "the job's tasks across every part":
    //   plan by PART  → send it, so only the piece the dialog was opened from moves;
    //   plan by FO    → omit it, so the job's tasks move, which is what by-FO means.
    // Sending it in a by-FO plan would shrink the move to one piece; omitting it in a by-part plan
    // would move every piece. Both are silent and both are wrong (see the trace of this change).
    const planIsByPart = currentPlanification?.unitCriteria?.name === 'Job'
    const scopesToPart = planIsByPart && isPresentId(editingEvent?.partId)
    const payload = {
      jobPriority: toJobPriorityPayload(newJobSettings.jobPriority),
      remark: newJobSettings.remark ?? '',
      preferenceDevice: newJobSettings.preferenceDevice ?? '',
      ...(canRelocate ? { planificationId: currentPlanification.id } : {}),
      ...(canRelocate && scopesToPart ? { partId: editingEvent.partId } : {})
    }

    let result
    await apiPut('planner/jobSettings/' + editingEvent.jobReferenceId, payload)
      .then((response) => {
        setJobSettings({ ...jobSettings, ...payload })
        setNewJobSettings({ ...newJobSettings, ...payload })
        result = response ?? {}
      })
      .catch((error) => {
        showAlertDialog({ title: showJobSettingsError(error), severity: 'error' })
      })
    if (!result) {
      return
    }

    if (result.warningCode) {
      showAlert({ message: t_(result.warningCode), severity: 'warning' })
    } else {
      showAlert({ message: t_('Updated correctly.'), severity: 'success' })
    }
    return true
  }

  const onAccept = async (closeDialog) => {
    let resultOk

    // S81/S82/R3: dispatched by the ACTIVE TAB'S NAME, never by its position. Inserting "Job
    // Settings" first shifted every index this switch used to read.
    switch (getEventDialogTabKey(selectedTab)) {
      case EVENT_DIALOG_TAB_KEYS.JOB_SETTINGS:
        resultOk = await saveJobSettings()
        break
      case EVENT_DIALOG_TAB_KEYS.PART_SETTINGS:
        resultOk = await onJobEditAccept()
        break
      case EVENT_DIALOG_TAB_KEYS.PIN_SETTING:
        resultOk = await savePinSettings()
        break
      default:
        break
    }

    if (closeDialog && resultOk) {
      resetValues()
    }
  }

  const resetValues = () => {
    setSelectedTab(DEFAULT_EVENT_DIALOG_TAB_INDEX)
    setShowJobSettingsErrors(false)
    setOpenDialog(false)
    reloadCalendar()
  }

  return (
    <>
      <SmartDialog
        title={{ render: <Typography /> }}
        setOpen={openDialog}
        cancelCallback={() => { resetValues() }}
        acceptCallback={() => onAccept(true)}
        renderComponent={
          <EventDialog
            event={editingEvent}
            setEvent={setEditingEvent}
            selectedTab={selectedTab}
            onChangeSelectedTab={(newTab) => onChangeSelectedTab(newTab)}
            onAccept={(closeDialog) => onAccept(closeDialog)}
            currentPlanification={currentPlanification}
            newPinJobSettings={newPinJobSettings}
            onChangeNewPinJobSettings={useCallback((newSettings) => setNewPinJobSettings(newSettings), [])}
            AlertElementDialog={AlertElementDialog}
            pinSettings={pinSettings}
            onChangePinSettings={useCallback((newSettings) => setPinSettings(newSettings), [])}
            jobSettings={jobSettings}
            newJobSettings={newJobSettings}
            onChangeNewJobSettings={useCallback((newSettings) => setNewJobSettings(newSettings), [])}
            showJobSettingsErrors={showJobSettingsErrors}
            setResourceFamily={setResourceFamily}
            calendarRef={calendarRef}
            isThisEventFamily={isThisEventFamily}
            setIsFamilyViewActive={setIsFamilyViewActive}
            setOpenDialog={setOpenDialog}
          />
        }
        // S83/R3: the size used to be picked by the indices of the two tabs that no longer exist,
        // which the new first tab would have shifted onto the real ones. The three tabs this dialog
        // renders share the same size.
        paperWidth='50rem'
        paperHeight='45%'
      />
    </>
  )
}
export default PlannerEventDialog
